import React, { useEffect, useRef } from 'react';
import { Badge, Popover, Button, List, Typography, Tag, Empty } from 'antd';
import { BellOutlined, PhoneOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import dayjs from 'dayjs';
import { notification } from 'antd';

interface NotificationBellProps {
  user: { id: string; role?: string } | null;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ user }) => {
  const navigate = useNavigate();
  const prevCountRef = useRef<number | null>(null);

  const isAgentOrSales = user?.role === 'admin' || user?.role === 'agent' || user?.role === 'developer' || user?.role === 'sales_rep_internal';

  const { data: callMeCountData } = useQuery(
    'call-me-count',
    () => api.get('/api/call-me/count').then((r) => r.data),
    { enabled: !!user && !!isAgentOrSales, retry: false, refetchInterval: 60000 }
  );

  const { data: callMeQueueData } = useQuery(
    'call-me-queue',
    () => api.get('/api/call-me', { params: { status: 'pending' } }).then((r) => r.data),
    { enabled: !!user && !!isAgentOrSales, retry: false, refetchInterval: 60000 }
  );

  const count = callMeCountData?.count ?? 0;
  const items = callMeQueueData?.items ?? [];

  // Popup notification when new Call Me arrives (count increases)
  useEffect(() => {
    if (!user || !isAgentOrSales || count === 0) return;
    const prev = prevCountRef.current;
    prevCountRef.current = count;
    if (prev !== null && count > prev) {
      notification.warning({
        message: 'New Call Me Request',
        description: `${count - prev} new Call Me request(s) in queue. Store needs assistance.`,
        placement: 'topRight',
        duration: 5,
        icon: <PhoneOutlined style={{ color: '#faad14' }} />,
        onClick: () => navigate('/tickets?tab=callme'),
      });
    }
  }, [count, user, isAgentOrSales, navigate]);

  if (!user || !isAgentOrSales) return null;

  const content = (
    <div style={{ width: 360, maxHeight: 400, overflow: 'auto' }}>
      <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
        <Typography.Text strong>Call Me Queue</Typography.Text>
        {count > 0 && (
          <Badge count={count} style={{ marginLeft: 8, backgroundColor: '#faad14' }} />
        )}
      </div>
      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pending requests" style={{ padding: 24 }} />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(item: any) => (
            <List.Item
              key={item.id}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/tickets?tab=callme`)}
              actions={[
                <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); navigate(`/workspace/new?fromCallMe=${item.id}`); }}>
                  Convert
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={<span>{item.business_name || 'Store'} · {item.store_id}</span>}
                description={
                  <span>
                    <Tag>{item.service_type || '—'}</Tag>
                    {dayjs(item.created_at).format('MMM D, h:mm A')}
                  </span>
                }
              />
            </List.Item>
          )}
        />
      )}
      <div style={{ paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
        <Button type="link" size="small" block onClick={() => navigate('/tickets?tab=callme')}>
          View all in Tickets
        </Button>
      </div>
    </div>
  );

  return (
    <Popover content={content} title={null} trigger="click" placement="bottomRight">
      <Badge count={count} size="small" offset={[-2, 2]} style={{ backgroundColor: count > 0 ? '#faad14' : undefined }}>
        <Button
          type="default"
          icon={<BellOutlined />}
          style={{ display: 'flex', alignItems: 'center' }}
        />
      </Badge>
    </Popover>
  );
};

export default NotificationBell;
